import React from 'react';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { contactInfo } from '../data/mock';

const Contact = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-brand-blue-50 via-blue-50 to-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Get In Touch
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Have questions? We're here to help! Contact us today for a free quote or to learn more about our services.
          </p>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Phone */}
            <Card className="border-2 hover:border-brand-blue-500 transition-all duration-200">
              <CardContent className="p-6 text-center space-y-4">
                <div className="w-14 h-14 bg-brand-blue-100 rounded-full flex items-center justify-center mx-auto">
                  <Phone className="h-7 w-7 text-brand-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Phone</h3>
                  <a href={`tel:${contactInfo.phone}`} className="text-brand-blue-600 hover:text-brand-blue-700 font-medium">
                    {contactInfo.phone}
                  </a>
                </div>
              </CardContent>
            </Card>

            {/* Email */}
            <Card className="border-2 hover:border-brand-blue-500 transition-all duration-200">
              <CardContent className="p-6 text-center space-y-4">
                <div className="w-14 h-14 bg-brand-blue-100 rounded-full flex items-center justify-center mx-auto">
                  <Mail className="h-7 w-7 text-brand-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Email</h3>
                  <a href={`mailto:${contactInfo.email}`} className="text-brand-blue-600 hover:text-brand-blue-700 font-medium break-all">
                    {contactInfo.email}
                  </a>
                </div>
              </CardContent>
            </Card>

            {/* Location */}
            <Card className="border-2 hover:border-brand-blue-500 transition-all duration-200">
              <CardContent className="p-6 text-center space-y-4">
                <div className="w-14 h-14 bg-brand-blue-100 rounded-full flex items-center justify-center mx-auto">
                  <MapPin className="h-7 w-7 text-brand-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Location</h3>
                  <p className="text-gray-600">{contactInfo.address}</p>
                </div>
              </CardContent>
            </Card>

            {/* Hours */}
            <Card className="border-2 hover:border-brand-gold transition-all duration-200">
              <CardContent className="p-6 text-center space-y-4">
                <div className="w-14 h-14 bg-brand-navy rounded-full flex items-center justify-center mx-auto">
                  <Clock className="h-7 w-7 text-brand-gold" />
                </div>
                <div>
                  <h3 className="font-semibold text-brand-navy mb-2">Availability</h3>
                  <p className="text-2xl font-bold text-brand-gold">24/7</p>
                  <p className="text-xs text-gray-600 mt-1">{contactInfo.hours.description}</p>
                  <p className="text-xs text-brand-blue mt-2 font-medium">Emergency Services Available</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Map and Form Section */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Map */}
            <Card className="border-2 overflow-hidden">
              <CardContent className="p-0">
                <div className="bg-gray-200 h-[400px] flex items-center justify-center">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d317895.73654853424!2d-97.32519977499997!3d49.8953535!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x52ea73c4a93e46e5%3A0x6a0c0b0a9f3e8d7a!2sWinnipeg%2C%20MB%2C%20Canada!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
                    width="100%"
                    height="400"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Winnipeg Location"
                  ></iframe>
                </div>
              </CardContent>
            </Card>

            {/* Quick Contact */}
            <Card className="border-2">
              <CardContent className="p-8 space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Ready to Get Started?
                  </h2>
                  <p className="text-gray-600">
                    Contact us today for a free quote and experience the Sparkle Property Care Ltd difference.
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="bg-brand-blue-50 p-6 rounded-lg space-y-4">
                    <h3 className="font-semibold text-gray-900">Why Choose Us?</h3>
                    <ul className="space-y-2 text-sm text-gray-700">
                      <li className="flex items-start space-x-2">
                        <span className="text-brand-blue-600 font-bold">✓</span>
                        <span>Professional and experienced team</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-brand-blue-600 font-bold">✓</span>
                        <span>Eco-friendly cleaning products</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-brand-blue-600 font-bold">✓</span>
                        <span>Flexible scheduling options</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-brand-blue-600 font-bold">✓</span>
                        <span>100% satisfaction guaranteed</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-brand-blue-600 font-bold">✓</span>
                        <span>Serving all of Winnipeg, MB</span>
                      </li>
                    </ul>
                  </div>

                  <div className="space-y-3">
                    <a href="tel:2042946802" className="block">
                      <Button size="lg" className="w-full bg-brand-blue-600 hover:bg-brand-blue-700 text-white">
                        <Phone className="mr-2 h-5 w-5" />
                        Call Now: 204-294-6802
                      </Button>
                    </a>
                    <a href="/book" className="block">
                      <Button size="lg" variant="outline" className="w-full border-2 border-brand-blue-600 text-brand-blue-600 hover:bg-brand-blue-50">
                        <Send className="mr-2 h-5 w-5" />
                        Book Appointment Online
                      </Button>
                    </a>
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <p className="text-sm text-gray-500 text-center">
                    We typically respond within 24 hours
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Serving All of Winnipeg
            </h2>
            <p className="text-lg text-gray-600">
              We proudly serve the following areas and more
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {[
              'Downtown', 'St. Vital', 'River Heights', 'Osborne Village',
              'Fort Garry', 'Transcona', 'Charleswood', 'St. James',
              'St. Boniface', 'Wolseley', 'Crescentwood', 'Tuxedo'
            ].map((area, idx) => (
              <div key={idx} className="bg-gray-50 p-3 rounded-lg text-center border border-gray-200">
                <p className="text-sm text-gray-700 font-medium">{area}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
